import { useEffect, useRef, useState } from "react";

export const useDraggable = ({
    initialX,
    initialY,
    boundary = true,
    onFocus
}) => {
    const [position, setPosition] = useState({
        x: initialX,
        y: initialY
    });

    const dragging = useRef(false);
    const offset = useRef({ x: 0, y: 0 });
    const ref = useRef(null);

    const startDrag = (clientX, clientY) => {
        if (!ref.current) return;

        dragging.current = true;

        offset.current = {
            x: clientX - position.x,
            y: clientY - position.y
        };

        document.body.style.userSelect = "none";

        if (onFocus) onFocus(); // bump z-index
    };

    const handleMouseDown = (e) => {
        startDrag(e.clientX, e.clientY);
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        startDrag(touch.clientX, touch.clientY);
    };

    const handleResize = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        setPosition({
            x: (window.innerWidth - rect.width) / 2 - offset.current.x,
            y: (window.innerHeight - rect.height) / 2 -offset.current.y
        });
    };
    // 🔥 NEW: Update when initial position changes
    useEffect(() => {
        setPosition({ x: initialX, y: initialY });
    }, [initialX, initialY]);
    
    useEffect(() => {
        const move = (clientX, clientY) => {
            if (!dragging.current || !ref.current) return;

            let newX = clientX - offset.current.x;
            let newY = clientY - offset.current.y;

            if (boundary) {
                const rect = ref.current.getBoundingClientRect();

                const maxX = window.innerWidth - rect.width;
                const maxY = window.innerHeight - rect.height;

                newX = Math.max(0, Math.min(newX, maxX));
                newY = Math.max(0, Math.min(newY, maxY));
            }

            setPosition({ x: newX, y: newY });
        };

        const handleMouseMove = (e) => move(e.clientX, e.clientY);

        const handleTouchMove = (e) => {
            const touch = e.touches[0];
            move(touch.clientX, touch.clientY);
        };

        const stopDrag = () => {
            dragging.current = false;
            document.body.style.userSelect = "";
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", stopDrag);
        window.addEventListener("touchmove", handleTouchMove);
        window.addEventListener("touchend", stopDrag);
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", stopDrag);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", stopDrag);
            window.removeEventListener("resize", handleResize);
        };
    }, [position, boundary]);

    return {
        ref,
        position,
        handleMouseDown,
        handleTouchStart
    };
};